import React from 'react';

// Textos de los íconos informativos de los reportes de nómina: de dónde sale cada cifra.

const pct = (v) => `${Number(v || 0).toLocaleString('es-CO', { maximumFractionDigits: 3 })} %`;
const pesos = (v) =>
  v == null ? '—' : Number(v).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

const Titulo = ({ children }) => <p className="font-semibold mb-1">{children}</p>;
const Lista = ({ children }) => <ul className="list-disc pl-4 space-y-0.5">{children}</ul>;
const Nota = ({ children }) => <p className="mt-1.5 text-muted-foreground">{children}</p>;

export const explicaDevengado = (alcance) => (
  <>
    <Titulo>Devengado = lo que ganan las personas antes de descuentos</Titulo>
    <Lista>
      <li>Básico: salario ÷ 30 × días del periodo (sin días antes del ingreso, incapacidades ni permisos no remunerados).</li>
      <li>Auxilio de transporte, si salario + recargos + extras está por debajo de 2 SMMLV.</li>
      <li>Recargos (nocturno, dominical y festivo) y horas extra.</li>
      <li>Incapacidades al % configurado y ajustes manuales a favor.</li>
    </Lista>
    {alcance && <Nota>{alcance}</Nota>}
  </>
);

export const explicaNeto = (
  <>
    <Titulo>Neto = lo que se le consigna a cada persona</Titulo>
    <p>Devengado menos sus deducciones: salud 4 % y pensión 4 % sobre el IBC (básico + recargos + extras + incapacidad, o solo básico + incapacidad si está activa la excepción del IBC), otros conceptos de deducción y ajustes en contra.</p>
  </>
);

/** `p`: parámetros de costo empresa vigentes; `t`: { devengado, aportes, prestaciones, costoEmpresa }. */
export const explicaCostoEmpresa = (p, t) => (
  <>
    <Titulo>Costo total empresa = devengado + aportes del empleador + prestaciones</Titulo>
    {t && (
      <p className="mb-1.5 tabular-nums">
        {pesos(t.devengado)} + {pesos(t.aportes)} + {pesos(t.prestaciones)} = <b>{pesos(t.costoEmpresa)}</b>
      </p>
    )}
    <p className="font-medium">
      Aportes del empleador, sobre el IBC ({p?.ibcSinRecargosExtras ? 'básico + incapacidad, por la excepción activa' : 'básico + recargos + extras + incapacidad'}):
    </p>
    <Lista>
      <li>Pensión {pct(p?.pensionPct)} · ARL {pct(p?.arlPct)} · caja de compensación {pct(p?.cajaPct)}.</li>
      <li>
        Salud {pct(p?.saludPct)}, ICBF {pct(p?.icbfPct)} y SENA {pct(p?.senaPct)}:{' '}
        {p?.exoneracion114 ? 'solo por quienes ganan 10 SMMLV o más (exoneración art. 114-1 E.T.).' : 'por todas las personas.'}
      </li>
    </Lista>
    <p className="font-medium mt-1">Prestaciones sociales (provisión, no pago del periodo):</p>
    <Lista>
      <li>
        Prima {pct(p?.primaPct)}, cesantías {pct(p?.cesantiasPct)} e intereses {pct(p?.interesesCesantiasPct)} sobre{' '}
        {p?.ibcSinRecargosExtras
          ? 'básico + incapacidad + auxilio de transporte (excepción activa: sin recargos ni extras).'
          : 'todo lo salarial (básico + recargos + extras + incapacidad) + auxilio de transporte.'}
      </li>
      <li>Vacaciones {pct(p?.vacacionesPct)} sobre el básico.</li>
    </Lista>
    <Nota>
      No se descuenta a la persona. Los porcentajes se cambian en Personal → Recargos y multiplicadores → Parámetros de
      nómina.
    </Nota>
  </>
);

export const explicaPorKg = (que = 'Devengado') => (
  <>
    <Titulo>{que} ÷ kilos recibidos</Titulo>
    <p>
      Kilos de la recepción de ropa sucia (peso registrado en cada lote) en las mismas plantas y fechas de cada nómina.
      Sirve para comparar el costo de la mano de obra con la producción.
    </p>
  </>
);

export const explicaPorHora = (
  <>
    <Titulo>Devengado ÷ horas trabajadas</Titulo>
    <p>Horas autorizadas de las marcaciones (o las marcadas, si aún no se autorizan). No incluye horas de incapacidad ni permisos.</p>
  </>
);

export const explicaRecargosExtras = (
  <>
    <Titulo>(Recargos + extras) ÷ devengado</Titulo>
    <Lista>
      <li>Recargos: horas dentro de la base que se trabajan de noche (35 %) o en domingo o festivo (hora + 90 %).</li>
      <li>Extras: horas por encima de las horas base de cada quincena (1,25 / 1,75 / 2,15 / 2,65).</li>
    </Lista>
  </>
);

export const explicaRepartoPlantas = (
  <>
    <Titulo>Cómo se reparte entre plantas</Titulo>
    <p>
      La nómina de cada persona se calcula completa y se reparte entre las plantas según las horas que marcó en cada una.
      Quien trabajó 80 % de sus horas en una planta le carga el 80 % de su costo.
    </p>
  </>
);

export const explicaParticipacion = (
  <>
    <Titulo>% del devengado vs. % de los kilos</Titulo>
    <p>
      Si una planta pesa más en costo que en kilos (barra azul más larga que la verde), su mano de obra es relativamente más
      cara por kilo que el promedio; si pesa menos, es más eficiente.
    </p>
  </>
);
